/**
 * Use Case: Should Filter Out Scheduled Media
 *
 * Mirrors Flutter's ContentUtils.shouldFilterOutScheduleMediaForCurrentUser.
 * Determines whether a scheduled / early-access media item should be hidden
 * from the current viewer.
 *
 * Rules (matching Flutter):
 *  1. Never filter for the owner (currentUid === media.firebaseId)
 *  2. Never filter if the media has no schedule
 *  3. Filter out if allowEarlyAccess is false
 *  4. Filter out if allowEarlyAccess is true but the viewer has no active
 *     exclusive-content subscription for this creator (mirrors
 *     exclusiveContentNotifier.hasActiveSubscription).
 *
 * The hasActiveSubscription predicate is injected by the caller — use cases
 * cannot import Redux. Hooks should source it from
 * selectHasActiveSubscription via useStore().
 */

import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

export type HasActiveSubscriptionPredicate = (creatorUserId: string) => boolean;

export class ShouldFilterOutScheduledMediaUseCase {
    execute(
        media: UserMediaEntity,
        currentUid: string | undefined,
        hasActiveSubscription: HasActiveSubscriptionPredicate,
    ): boolean {
        if (currentUid && media.firebaseId && currentUid === media.firebaseId) {
            return false; // Owner always sees their own content
        }
        if (media.schedule == null) {
            return false; // No schedule — visible to everyone
        }
        if (!media.allowEarlyAccess) {
            return true;
        }
        // Early access enabled — only viewers with an active subscription to
        // this creator may see it before the schedule lands.
        if (!media.firebaseId) {
            return true;
        }
        return !hasActiveSubscription(media.firebaseId);
    }
}

export const shouldFilterOutScheduledMediaUseCase = new ShouldFilterOutScheduledMediaUseCase();
